import React, { useState, useEffect, useCallback } from 'react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useOrganization, OrganizationMember, OrganizationInvite, OrganizationRole } from '../context/OrganizationContext';
import { useSente, SenteWebhook } from '../hooks/useSente';

const WEBHOOK_EVENTS = ['message.received', 'run.completed', 'run.failed', 'run.blocked'];

const Settings: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  // Organization state
  const {
    currentOrganization,
    isOwner,
    getMembers,
    updateOrganization,
    updateMemberRole,
    removeMember,
    getPendingInvites,
    createInvite,
    cancelInvite
  } = useOrganization();
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [orgInvites, setOrgInvites] = useState<OrganizationInvite[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrganizationRole>('member');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState<{ memberId: string; memberName: string } | null>(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [isEditingOrgName, setIsEditingOrgName] = useState(false);
  const [editedOrgName, setEditedOrgName] = useState('');
  const [isSavingOrgName, setIsSavingOrgName] = useState(false);

  // Sente webhooks state
  const { ready, listWebhooks, createWebhook, deleteWebhook } = useSente();
  const [webhooks, setWebhooks] = useState<SenteWebhook[]>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhookError, setWebhookError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>(['message.received']);
  const [isAddingWebhook, setIsAddingWebhook] = useState(false);
  const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(null);
  const [deleteWebhookId, setDeleteWebhookId] = useState<string | null>(null);

  // Fetch organization members and invites
  const fetchOrgData = useCallback(async () => {
    if (!currentOrganization) return;

    setOrgLoading(true);
    setOrgError(null);
    try {
      // All members can see the members list
      const members = await getMembers(currentOrganization.id);
      setOrgMembers(members);

      // Only owners can see pending invites
      if (isOwner) {
        const invites = await getPendingInvites(currentOrganization.id);
        setOrgInvites(invites);
      }
    } catch (err) {
      console.error('Failed to fetch organization data:', err);
      setOrgError('Failed to load organization data');
    } finally {
      setOrgLoading(false);
    }
  }, [currentOrganization, isOwner, getMembers, getPendingInvites]);

  // Load org data when organization changes
  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData]);

  // Load Sente webhooks once the org is selected
  useEffect(() => {
    if (!ready) return;
    setWebhooksLoading(true);
    setWebhookError(null);
    listWebhooks()
      .then(setWebhooks)
      .catch((err: any) => setWebhookError(err.message))
      .finally(() => setWebhooksLoading(false));
  }, [ready, listWebhooks]);

  const toggleWebhookEvent = (event: string) => {
    setWebhookEvents(prev =>
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
    );
  };

  const handleAddWebhook = async () => {
    if (!webhookUrl.trim() || webhookEvents.length === 0) return;
    setIsAddingWebhook(true);
    setWebhookError(null);
    try {
      const created = await createWebhook({ url: webhookUrl.trim(), events: webhookEvents });
      setNewWebhookSecret(created.secret);
      setWebhooks(prev => [...prev, created]);
      setWebhookUrl('');
      setWebhookEvents(['message.received']);
    } catch (err: any) {
      setWebhookError(err.message);
    } finally {
      setIsAddingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    setWebhookError(null);
    try {
      await deleteWebhook(id);
      setWebhooks(prev => prev.filter(w => w.id !== id));
    } catch (err: any) {
      setWebhookError(err.message);
    } finally {
      setDeleteWebhookId(null);
    }
  };

  // Send invite
  const handleSendInvite = async () => {
    if (!currentOrganization || !inviteEmail.trim()) return;

    setIsSendingInvite(true);
    setOrgError(null);
    try {
      await createInvite(currentOrganization.id, inviteEmail.trim(), inviteRole);
      setInviteEmail('');
      setInviteRole('member');
      await fetchOrgData();
    } catch (err: any) {
      setOrgError(err.response?.data?.error || 'Failed to send invite');
    } finally {
      setIsSendingInvite(false);
    }
  };

  // Cancel invite
  const handleCancelInvite = async (inviteId: string) => {
    if (!currentOrganization) return;

    try {
      await cancelInvite(currentOrganization.id, inviteId);
      setOrgInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (err) {
      setOrgError('Failed to cancel invite');
    }
  };

  // Update member role
  const handleUpdateRole = async (userId: string, role: OrganizationRole) => {
    if (!currentOrganization) return;

    try {
      await updateMemberRole(currentOrganization.id, userId, role);
      setOrgMembers(prev => prev.map(m =>
        m.userId === userId ? { ...m, role } : m
      ));
    } catch (err: any) {
      setOrgError(err.response?.data?.error || 'Failed to update member role');
    }
  };

  // Remove member
  const handleRemoveMember = async () => {
    if (!currentOrganization || !showRemoveModal) return;

    setIsRemovingMember(true);
    try {
      await removeMember(currentOrganization.id, showRemoveModal.memberId);
      setOrgMembers(prev => prev.filter(m => m.userId !== showRemoveModal.memberId));
      setShowRemoveModal(null);
    } catch (err: any) {
      setOrgError(err.response?.data?.error || 'Failed to remove member');
    } finally {
      setIsRemovingMember(false);
    }
  };

  // Start editing organization name
  const handleStartEditOrgName = () => {
    if (currentOrganization) {
      setEditedOrgName(currentOrganization.name);
      setIsEditingOrgName(true);
    }
  };

  // Save organization name
  const handleSaveOrgName = async () => {
    if (!currentOrganization || !editedOrgName.trim()) return;
    if (editedOrgName.trim() === currentOrganization.name) {
      setIsEditingOrgName(false);
      return;
    }

    setIsSavingOrgName(true);
    setOrgError(null);
    try {
      await updateOrganization(currentOrganization.id, { name: editedOrgName.trim() });
      setIsEditingOrgName(false);
    } catch (err: any) {
      setOrgError(err.response?.data?.error || 'Failed to update organization name');
    } finally {
      setIsSavingOrgName(false);
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    const name = user?.name || user?.nickname || user?.email || 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Checkbox component for consistent styling
  const Checkbox: React.FC<{
    checked: boolean;
    onChange: () => void;
    label: string;
    description?: string;
    disabled?: boolean;
  }> = ({ checked, onChange, label, description, disabled }) => (
    <label className={`flex items-start space-x-3 group ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
      <div className="relative flex items-center justify-center mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only"
        />
        <div className={`w-5 h-5 rounded border-2 transition-all duration-150 flex items-center justify-center ${
          checked
            ? 'bg-gray-900 border-gray-900 dark:bg-blue-600 dark:border-blue-600'
            : 'border-gray-300 dark:border-slate-600 group-hover:border-gray-400 dark:group-hover:border-slate-500'
        }`}>
          {checked && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
        {description && (
          <div className="text-sm text-gray-500 dark:text-slate-400">{description}</div>
        )}
      </div>
    </label>
  );

  return (
    <div className="h-full overflow-hidden flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-950 dark:to-slate-900 transition-colors duration-300">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Breadcrumbs */}
            <Breadcrumbs
              items={[{ label: 'Settings' }]}
              className="mb-6"
            />

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">Settings</h1>
              <p className="text-sm text-gray-500 dark:text-slate-400">Manage your account and application preferences</p>
            </div>

            {/* Organization Section - Visible to all members */}
            {currentOrganization && (
              <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 overflow-hidden shadow-sm mb-6">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Organization</h2>
                      {isEditingOrgName ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editedOrgName}
                            onChange={(e) => setEditedOrgName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveOrgName();
                              if (e.key === 'Escape') setIsEditingOrgName(false);
                            }}
                            autoFocus
                            className="px-2 py-1 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                          />
                          <button
                            onClick={handleSaveOrgName}
                            disabled={isSavingOrgName || !editedOrgName.trim()}
                            className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title="Save"
                          >
                            {isSavingOrgName ? (
                              <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => setIsEditingOrgName(false)}
                            disabled={isSavingOrgName}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                            title="Cancel"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-700 dark:text-slate-300">{currentOrganization.name}</p>
                          {isOwner && (
                            <button
                              onClick={handleStartEditOrgName}
                              className="p-1 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
                              title="Edit organization name"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {orgLoading && (
                      <div className="w-4 h-4 border-2 border-gray-300 dark:border-slate-600 border-t-gray-600 dark:border-t-slate-300 rounded-full animate-spin" />
                    )}
                  </div>
                </div>
                <div className="p-6">
                  {orgError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">{orgError}</p>
                    </div>
                  )}

                  <div className={`grid grid-cols-1 ${isOwner ? 'lg:grid-cols-2' : ''} gap-8`}>
                    {/* Members Section */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        Members ({orgMembers.length})
                      </h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {orgMembers.map(member => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-lg"
                          >
                            <div className="flex items-center space-x-3 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {(member.user.name || member.user.email || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {member.user.name || member.user.email || 'Unknown'}
                                </div>
                                {member.user.name && member.user.email && (
                                  <div className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                    {member.user.email}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {member.userId === user?.sub ? (
                                <span className="text-xs text-gray-500 dark:text-slate-400 px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded">
                                  You ({member.role})
                                </span>
                              ) : isOwner ? (
                                <>
                                  <select
                                    value={member.role}
                                    onChange={(e) => handleUpdateRole(member.userId, e.target.value as OrganizationRole)}
                                    className="text-xs px-2 py-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value="member">Member</option>
                                    <option value="owner">Owner</option>
                                  </select>
                                  {member.role !== 'owner' && (
                                    <button
                                      onClick={() => setShowRemoveModal({ memberId: member.userId, memberName: member.user.name || member.user.email || 'this member' })}
                                      className="p-1 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      title="Remove member"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  member.role === 'owner'
                                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                    : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'
                                }`}>
                                  {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                        {orgMembers.length === 0 && !orgLoading && (
                          <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">
                            No members found
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Invites Section - Owner Only */}
                    {isOwner && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Invite Members
                        </h3>

                        {/* Send Invite Form */}
                        <div className="flex flex-col sm:flex-row gap-2 mb-4">
                          <input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="Email address"
                            className="flex-1 px-3 py-2 text-sm bg-white dark:bg-slate-800/50 border border-gray-200 dark:border-slate-600/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                          />
                          <div className="flex gap-2">
                            <select
                              value={inviteRole}
                              onChange={(e) => setInviteRole(e.target.value as OrganizationRole)}
                              className="flex-1 sm:flex-none px-3 py-2 text-sm bg-white dark:bg-slate-800/50 border border-gray-200 dark:border-slate-600/50 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                            >
                              <option value="member">Member</option>
                              <option value="owner">Owner</option>
                            </select>
                            <button
                              onClick={handleSendInvite}
                              disabled={isSendingInvite || !inviteEmail.trim()}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              {isSendingInvite ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                'Invite'
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Pending Invites */}
                        {orgInvites.length > 0 && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-slate-400 mb-2">Pending Invites</div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {orgInvites.map(invite => (
                                <div
                                  key={invite.id}
                                  className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30 rounded-lg"
                                >
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {invite.email}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-slate-400">
                                      {invite.role} &middot; Expires {new Date(invite.expiresAt).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleCancelInvite(invite.id)}
                                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {orgInvites.length === 0 && (
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            No pending invites. Invited members will receive an email with a link to join.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Account Section */}
                <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 overflow-hidden shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Account</h2>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg">
                        {getUserInitials()}
                      </div>
                      <div>
                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                          {user?.name || user?.nickname || 'User'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-slate-400">
                          {user?.email}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Appearance Section */}
                <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 overflow-hidden shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Appearance</h2>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">Theme</div>
                        <div className="text-sm text-gray-500 dark:text-slate-400">Choose between light and dark mode</div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => theme === 'dark' && toggleTheme()}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            theme === 'light'
                              ? 'bg-gray-900 text-white dark:bg-blue-600'
                              : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <span>Light</span>
                          </div>
                        </button>
                        <button
                          onClick={() => theme === 'light' && toggleTheme()}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            theme === 'dark'
                              ? 'bg-gray-900 text-white dark:bg-blue-600'
                              : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                            <span>Dark</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* About Section */}
                <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 overflow-hidden shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">About</h2>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-slate-400">Version</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">1.0.0</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-slate-400">Build</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Production</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Webhooks Section */}
                <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 overflow-hidden shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Webhooks</h2>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          Sente calls these URLs when your identities receive mail or runs change state.
                        </p>
                      </div>
                      {webhooksLoading && (
                        <div className="w-4 h-4 border-2 border-gray-300 dark:border-slate-600 border-t-gray-600 dark:border-t-slate-300 rounded-full animate-spin" />
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    {webhookError && (
                      <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-600 dark:text-red-400">{webhookError}</p>
                      </div>
                    )}

                    {/* One-time secret display */}
                    {newWebhookSecret && (
                      <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                          Store this — it verifies every delivery (X-Sente-Secret header). It won't be shown again.
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-yellow-200 dark:border-yellow-800 rounded text-xs font-mono text-gray-900 dark:text-white break-all">
                            {newWebhookSecret}
                          </code>
                          <button
                            onClick={() => navigator.clipboard.writeText(newWebhookSecret)}
                            className="px-3 py-2 bg-gray-900 dark:bg-blue-600 hover:bg-gray-800 dark:hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            Copy
                          </button>
                          <button
                            onClick={() => setNewWebhookSecret(null)}
                            className="px-3 py-2 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 text-xs font-medium rounded-lg transition-colors"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Webhook list */}
                    {webhooks.length > 0 ? (
                      <div className="space-y-2 mb-6">
                        {webhooks.map(webhook => (
                          <div
                            key={webhook.id}
                            className="flex items-start justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-lg"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {webhook.url}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {webhook.events.map(event => (
                                  <span
                                    key={event}
                                    className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs rounded"
                                  >
                                    {event}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                              {deleteWebhookId === webhook.id ? (
                                <>
                                  <button
                                    onClick={() => handleDeleteWebhook(webhook.id)}
                                    className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setDeleteWebhookId(null)}
                                    className="px-2 py-1 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300 text-xs rounded hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setDeleteWebhookId(webhook.id)}
                                  className="p-1.5 text-gray-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                  title="Delete webhook"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      !webhooksLoading && (
                        <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
                          No webhooks yet. Add one to get notified on inbound mail.
                        </p>
                      )
                    )}

                    {/* Add webhook form */}
                    <div className="border-t border-gray-100 dark:border-slate-700/50 pt-5">
                      <div className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">Add webhook</div>
                      <input
                        type="url"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://example.com/webhooks/sente"
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800/50 border border-gray-200 dark:border-slate-600/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 mb-4"
                      />
                      <div className="space-y-3 mb-4">
                        {WEBHOOK_EVENTS.map(event => (
                          <Checkbox
                            key={event}
                            checked={webhookEvents.includes(event)}
                            onChange={() => toggleWebhookEvent(event)}
                            label={event}
                            disabled={isAddingWebhook}
                          />
                        ))}
                      </div>
                      <button
                        onClick={handleAddWebhook}
                        disabled={!ready || isAddingWebhook || !webhookUrl.trim() || webhookEvents.length === 0}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isAddingWebhook ? 'Adding...' : 'Add webhook'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remove Member Modal */}
      {showRemoveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRemoveModal(null)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Remove Member
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
              Are you sure you want to remove <strong>{showRemoveModal.memberName}</strong> from this organization? They will lose access to all projects and resources.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowRemoveModal(null)}
                disabled={isRemovingMember}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveMember}
                disabled={isRemovingMember}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isRemovingMember ? 'Removing...' : 'Remove Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
